import { keyframes } from '@emotion/react';
import { Box, Button, Flex, Modal, OptimizeImage, Overlay, Text, Title } from '@inflearn/ds-react';
import { useDisclosure } from '@mantine/hooks';
import { random, range } from 'lodash-es';
import { useEffect, useState } from 'react';
import { NEXT_PUBLIC_CDN_INTEGRATED_HOST } from '../../../appConfig';
import { useCustomQueryParams } from '../../hooks/useCustomQueryParams';
import { eventNames } from '../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../utils/GTM/useTrackMixpanelEvent';

export const GiftCourseWelcomeModal = () => {
  const [isOpened, { open, close }] = useDisclosure(false);
  const { queryParams, replace } = useCustomQueryParams({
    queryParamKeys: ['payerName'],
  });

  const [payerName, setPayerName] = useState('');

  useEffect(() => {
    const payerName = queryParams.payerName;
    if (payerName) {
      setPayerName(payerName);
      open();
      replace({ payerName: undefined });
    }
  }, [queryParams.payerName, open, replace]);

  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const handleClickNowStudyButton = () => {
    close();

    trackMixpanelEvent(eventNames.CLICK_VIEW_MYCOURSES);
  };

  return (
    <Modal.Root
      opened={isOpened}
      onClose={close}
      trapFocus={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Modal.Overlay>
        <Confettic count={100} />
      </Modal.Overlay>
      <Modal.Content>
        <Modal.Header>
          <Title
            order={1}
            color="dark.9"
            weight="default"
            ta="center"
            w="100%"
            css={{
              whiteSpace: 'pre-wrap',
            }}
            size="h4"
          >
            {payerName} 님의 응원으로,{'\n'}당신의 배움이 시작됩니다.
          </Title>
        </Modal.Header>
        <Modal.Body>
          <Flex justify="center" align="center">
            <OptimizeImage
              src={`${NEXT_PUBLIC_CDN_INTEGRATED_HOST}/image/apng/handshake.png`}
              width={112}
              height={112}
              w={112}
            />
          </Flex>
          <Text
            size="md"
            align="center"
            color="gray.9"
            mt={16}
            css={{
              whiteSpace: 'pre-wrap',
            }}
          >
            {payerName}님이 당신의 가능성을 믿고 선물한 강의에요.{'\n'}그 믿음에 응답하는 시간,
            지금부터 시작해보세요.
          </Text>
        </Modal.Body>
        <Modal.Footer align="grow">
          <Button variant="filled" size="md" mt={8} onClick={handleClickNowStudyButton}>
            바로 학습하기
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

const COLORS = [
  '#057f75',
  '#a52626',
  '#000080',
  '#ff8f8f',
  '#0060ff',
  '#044640',
  '#a1dcc8',
  '#eb5757',
  '#b8dcff',
];

const Confettic = ({ count }: { count: number }) => {
  return (
    <Overlay zIndex={202} css={{ background: 'unset' }}>
      {range(count).map((i) => {
        const delay = random(0, 10, false);

        return (
          <Box
            key={i}
            css={{
              top: '-16px',
              position: 'fixed',
              animationName: fall,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              animationPlayState: 'running',
              left: `${random(0, 100, false)}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${random(2, 5, false)}s`,
            }}
          >
            <Box
              css={{
                width: '4px',
                height: '8px',
                animationName: shake,
                animationTimingFunction: 'cubic-bezier(0.445, 0.05, 0.55, 0.95)',
                animationIterationCount: 'infinite',
                animationPlayState: 'running',
                willChange: 'transform',
                background: COLORS[i % COLORS.length],
                animationDelay: `${delay}s`,
                animationDuration: `${random(1, 4, false)}s`,
              }}
            ></Box>
          </Box>
        );
      })}
    </Overlay>
  );
};

const fall = keyframes`
    0% {
        transform: translateY(0px);
    }
    100% {
        transform: translateY(calc(100vh + 16px));
    }`;

const shake = keyframes`
    0% {
        transform: translateX(0px) rotate(-30deg);
    }
    50% {
        transform: translateX(80px) rotate(30deg);
    }
    100% {
        transform: translateX(0px) rotate(-30deg);
    }
`;
